import React, { memo, useMemo, useState, useEffect } from 'react';
import { View, Text, TextInput, ScrollView, StyleSheet, Platform, Keyboard } from 'react-native';
import { TagPicker } from './TagPicker';
import { AttachmentPicker } from './AttachmentPicker';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { CorkBoardTag, CorkBoardAttachment, LocalAttachment } from '@/types/residents/corkBoard';
import type { ThreadFormErrors } from '@/hooks/residents/corkBoard/useThreadForm';

interface CreateThreadFormProps {
  mode: 'create' | 'edit';
  tags: CorkBoardTag[];

  title: string;
  onChangeTitle: (text: string) => void;
  body: string;
  onChangeBody: (text: string) => void;
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;

  attachments: LocalAttachment[];
  existingAttachments: CorkBoardAttachment[];
  onAddFiles: (files: LocalAttachment[]) => void;
  onRemoveFile: (index: number) => void;
  onRemoveExistingAttachment: (id: string) => void;

  errors: ThreadFormErrors;
  hasAttemptedSubmit: boolean;
  isSubmitting: boolean;
  uploadProgress?: number | null;
  submitError: string | null;

  /** Rendered inside the scroll view above the tag picker. */
  header?: React.ReactNode;
  /** Rendered inside the scroll view below the attachment picker. */
  footer?: React.ReactNode;
}

export const CreateThreadForm: React.FC<CreateThreadFormProps> = memo(({
  mode,
  tags,
  title,
  onChangeTitle,
  body,
  onChangeBody,
  selectedTagIds,
  onToggleTag,
  attachments,
  existingAttachments,
  onAddFiles,
  onRemoveFile,
  onRemoveExistingAttachment,
  errors,
  hasAttemptedSubmit,
  isSubmitting,
  uploadProgress,
  submitError,
  header,
  footer,
}) => {
  const { colors, semantic } = useThemeColors();
  const showErrors = hasAttemptedSubmit;

  const categoryTags = useMemo(
    () => tags.filter((t) => t.slug !== 'announcements'),
    [tags],
  );

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    const showSub = Keyboard.addListener('keyboardWillShow', (e) => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardHeight(0);
    });
    return () => { showSub.remove(); hideSub.remove(); };
  }, []);

  const iosInset = keyboardHeight > 0 ? { bottom: keyboardHeight } : undefined;

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
      contentInset={iosInset}
      scrollIndicatorInsets={iosInset}
    >
      {submitError ? (
        <View style={[styles.submitErrorBanner, { backgroundColor: colors.accent.redBg }]}>
          <Text style={[styles.submitErrorText, { color: semantic.interactive.danger }]}>{submitError}</Text>
        </View>
      ) : null}

      {header}

      {/* Tags */}
      <TagPicker
        tags={categoryTags}
        selectedTagIds={selectedTagIds}
        onToggleTag={onToggleTag}
        error={showErrors ? errors.tags : undefined}
      />

      {/* Title */}
      <View style={styles.fieldContainer}>
        <View style={styles.labelRow}>
          <Text style={[styles.label, { color: semantic.text.primary }]}>Title</Text>
          <Text style={[styles.charCounter, { color: semantic.text.muted }]}>
            {title.length}/{CORK_BOARD_INPUT_LIMITS.titleMax}
          </Text>
        </View>
        <TextInput
          style={[styles.textInput, { color: semantic.text.primary, backgroundColor: semantic.background.input, borderColor: showErrors && errors.title ? semantic.interactive.danger : semantic.border.input }]}
          value={title}
          onChangeText={onChangeTitle}
          placeholder="Enter a title..."
          placeholderTextColor={semantic.text.muted}
          maxLength={CORK_BOARD_INPUT_LIMITS.titleMax}
          editable={!isSubmitting}
          autoCapitalize="sentences"
          returnKeyType="next"
        />
        {showErrors && errors.title ? (
          <Text style={[styles.errorText, { color: semantic.interactive.danger }]}>{errors.title}</Text>
        ) : null}
      </View>

      {/* Body */}
      <View style={styles.fieldContainer}>
        <View style={styles.labelRow}>
          <Text style={[styles.label, { color: semantic.text.primary }]}>Description</Text>
          <Text style={[styles.charCounter, { color: semantic.text.muted }]}>
            {body.length}/{CORK_BOARD_INPUT_LIMITS.bodyMax}
          </Text>
        </View>
        <TextInput
          style={[
            styles.textInput,
            { color: semantic.text.primary, backgroundColor: semantic.background.input, borderColor: showErrors && errors.body ? semantic.interactive.danger : semantic.border.input },
            styles.bodyInput,
          ]}
          value={body}
          onChangeText={onChangeBody}
          placeholder="Write your post..."
          placeholderTextColor={semantic.text.muted}
          maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
          editable={!isSubmitting}
          multiline
          textAlignVertical="top"
          autoCapitalize="sentences"
        />
        {showErrors && errors.body ? (
          <Text style={[styles.errorText, { color: semantic.interactive.danger }]}>{errors.body}</Text>
        ) : null}
      </View>

      <AttachmentPicker
        attachments={attachments}
        existingAttachments={existingAttachments}
        onAddFiles={onAddFiles}
        onRemoveFile={onRemoveFile}
        onRemoveExistingAttachment={onRemoveExistingAttachment}
        isSubmitting={isSubmitting}
        uploadProgress={uploadProgress}
        error={showErrors ? errors.files : undefined}
      />

      {footer}
    </ScrollView>
  );
});

CreateThreadForm.displayName = 'CreateThreadForm';

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 40,
    gap: 20,
  },
  fieldContainer: {
    gap: 6,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  charCounter: {
    fontSize: 12,
  },
  textInput: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
  },
  bodyInput: {
    minHeight: 140,
    paddingTop: 12,
  },
  errorText: {
    fontSize: 12,
  },
  submitErrorBanner: {
    borderRadius: 8,
    padding: 12,
  },
  submitErrorText: {
    fontSize: 13,
    textAlign: 'center',
  },
});
